import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from './AppHeader';
import { ProfileHeader } from './ProfileHeader';
import { UserReviewsCard } from './UserReviewsCard';
import { UserTagsCard } from './UserTagsCard';
import { ProfileStats } from './ProfileStats';
import { MatchHistory } from './MatchHistory';
import { ProfileTabs, TabType, SubSectionType } from './ProfileTabs';
import { AchievementGrid } from './achievements/AchievementGrid';
import { UserBadges } from './UserBadges';
import { Rankings } from './Rankings';
import { GlobalStats } from './GlobalStats';
import { UnifiedHelpButton, SpecificHelpModal } from './Help';
import StatsHistory from './StatsHistory';
import { UserReviewsResponse, UserMatchWithReviews, GamificationStats } from '../types';
import type { Achievement } from '../types/achievement';
import './ProfilePage.css';

interface UserStats {
  totalGoals: number;
  totalAssists: number;
  totalSaves: number;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  mvpCount: number;
  topScorerCount: number;
  topAssisterCount: number;
  topSaverCount: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  position?: string;
  rating: number;
  avatar?: string;
  preferredFoot?: string;
  preferredPosition?: string;
  description?: string;
}

const PublicProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { isMobile } = useResponsive();
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [matchHistory, setMatchHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [activeSubSection, setActiveSubSection] = useState<SubSectionType>('fifa-card');
  const [userReviews, setUserReviews] = useState<UserReviewsResponse | null>(null);
  const [gamificationStats, setGamificationStats] = useState<GamificationStats | null>(null);
  const [userBadges, setUserBadges] = useState<any[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [rankings, setRankings] = useState<any>(null);
  const [showBadges, setShowBadges] = useState(false);
  const [showRankings, setShowRankings] = useState(false);
  const [showGlobalStats, setShowGlobalStats] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [fetchingReviews, setFetchingReviews] = useState(false);
  const [fetchingStats, setFetchingStats] = useState(false);
  const [fetchingBadges, setFetchingBadges] = useState(false);
  const [profileInitialized, setProfileInitialized] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState<'none' | 'friends' | 'pending_sent' | 'pending_received' | 'rejected' | null>(null);
  const [sendingFriendRequest, setSendingFriendRequest] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();

  const isOwnProfile = authUser && user && authUser.id === user.id;

  // Cargar datos cuando el ID esté disponible
  useEffect(() => {
    if (id && !user) {
      setDataLoaded(false);
      setFetchingData(false);
      setFetchingReviews(false);
      setFetchingStats(false);
      setFetchingBadges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  // Cargar datos principales cuando el ID esté disponible
  useEffect(() => {
    if (profileInitialized) return;
    
    if (id && !dataLoaded) {
      setProfileInitialized(true);
      fetchUserData();
      fetchUserReviews();
      fetchGamingStats();
      fetchUserBadges();
      setDataLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dataLoaded, profileInitialized]);

  // Recargar datos cuando cambie la página
  useEffect(() => {
    if (user?.id && dataLoaded) {
      fetchUserData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, user?.id, dataLoaded]);

  // Recargar badges cuando cambie la pestaña
  useEffect(() => {
    if (activeTab === 'rewards' && user?.id && dataLoaded) {
      fetchUserBadges();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id, dataLoaded]);

  // Estado de amistad con el usuario del perfil (solo si no es el propio perfil y hay usuario logueado)
  useEffect(() => {
    if (!id || !authUser || isOwnProfile) {
      setFriendshipStatus(null);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch(`${getApiUrl('/api/friends/status')}/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setFriendshipStatus(data.status ?? 'none'))
      .catch(() => setFriendshipStatus('none'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authUser?.id, isOwnProfile]);

  const sendFriendRequest = async () => {
    if (!id || sendingFriendRequest) return;
    setSendingFriendRequest(true);
    try {
      const res = await fetch(getApiUrl('/api/friends/send-request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ receiverId: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setFriendshipStatus('pending_sent');
        showNotification('Solicitud enviada', 'success');
      } else {
        showNotification(data.error || 'Error al enviar solicitud', 'error');
      }
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setSendingFriendRequest(false);
    }
  };

  const fetchUserData = async () => {
    if (fetchingData || !id) {
      return;
    }
    try {
      setFetchingData(true);
      
      // Cargar información básica del usuario
      const userResponse = await fetch(`${getApiUrl('/api/users')}/${id}/public`);
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser({ ...userData, email: userData.email || '' });
        
        // Cargar estadísticas
        const statsResponse = await fetch(`${getApiUrl('/api/users')}/${id}/public-stats`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData.stats || statsData);
        }

        // Cargar historial de partidos
        const historyResponse = await fetch(
          `${getApiUrl('/api/users')}/${id}/public-match-history?page=${currentPage}&limit=5`
        );
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setMatchHistory(historyData.matches || []);
          setTotalPages(historyData.pagination?.pages || 1);
        }
      } else {
        showNotification('Usuario no encontrado', 'error');
        navigate('/home');
      }
    } catch (error) {
      showNotification('Error cargando datos del perfil', 'error');
    } finally {
      setLoading(false);
      setFetchingData(false);
    }
  };

  const fetchUserReviews = async () => {
    if (fetchingReviews || !id) {
      return;
    }
    try {
      setFetchingReviews(true);
      const response = await fetch(`${getApiUrl('/api/users')}/${id}/public-reviews`);
      
      if (response.ok) {
        const data = await response.json();
        setUserReviews(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando reviews', 'error');
      }
    } catch (error) {
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingReviews(false);
    }
  };

  const fetchGamingStats = async () => {
    if (fetchingStats || !id) {
      return;
    }
    try {
      setFetchingStats(true);
      
      // Obtener estadísticas básicas
      const statsResponse = await fetch(`${getApiUrl('/api/users')}/${id}/public-stats`);
      let stats = null;
      if (statsResponse.ok) {
        stats = await statsResponse.json();
      }
      
      // Obtener tags de reviews
      const tagsResponse = await fetch(`${getApiUrl('/api/users')}/${id}/public-tags-summary`);
      let tagsData = null;
      if (tagsResponse.ok) {
        tagsData = await tagsResponse.json();
      }
      
      // Combinar datos y mapear estructura de tags
      const combinedStats = {
        ...stats,
        topPositiveTags: (tagsData?.topPositive || []).map((tag: any) => ({ tag: tag.name, count: tag.count })),
        topNegativeTags: (tagsData?.topNegative || []).map((tag: any) => ({ tag: tag.name, count: tag.count })),
        topCustomPositiveTags: (tagsData?.topCustomPositive || []).map((tag: any) => ({ tag: tag.name, count: tag.count })),
        topCustomNegativeTags: (tagsData?.topCustomNegative || []).map((tag: any) => ({ tag: tag.name, count: tag.count })),
        totalPositiveTags: tagsData?.totals?.totalPositiveTags || 0,
        totalNegativeTags: tagsData?.totals?.totalNegativeTags || 0,
        totalReviews: tagsData?.totals?.totalReviews || 0
      };
      
      setGamificationStats(combinedStats);
    } catch (error) {
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingStats(false);
    }
  };

  const fetchUserBadges = async () => {
    if (fetchingBadges || !id) {
      return;
    }
    try {
      setFetchingBadges(true);
      const response = await fetch(`${getApiUrl('/api/users')}/${id}/badges`);

      if (response.ok) {
        const data = await response.json();
        setUserBadges(data.badges || []);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando badges', 'error');
      }

      try {
        const achievementsRes = await fetch(`${getApiUrl('/api/users')}/${id}/achievements`);
        if (achievementsRes.ok) {
          const json = await achievementsRes.json();
          setAchievements(json.achievements || []);
        }
      } catch {
        // best-effort; locked-state grid is still rendered from the catalog
      }
    } catch (error) {
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingBadges(false);
    }
  };

  const fetchRankings = async () => {
    try {
      const response = await fetch(`${getApiUrl('/api/users')}/${id}/rankings`);
      
      if (response.ok) {
        const data = await response.json();
        setRankings(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando rankings', 'error');
      }
    } catch (error) {
      showNotification('Error de conexión', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPositionEmoji = (position: string) => {
    switch (position) {
      case 'Arquero': return '🥅';
      case 'Defensor': return '🛡️';
      case 'Mediocampista': return '⚽';
      case 'Delantero': return '🚀';
      default: return '👤';
    }
  };

  const getMatchResultColor = (matchResult?: 'WIN' | 'LOSS' | 'DRAW') => {
    switch (matchResult) {
      case 'WIN': return '#1a2e1a';
      case 'LOSS': return '#2e1a1a';
      case 'DRAW': return '#2a2a2a';
      default: return '#2C2F33';
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div>Cargando perfil...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-error">
        <div>No se pudo cargar el perfil</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <AppHeader 
        title={isOwnProfile ? "Mi Perfil" : `Perfil de ${user.name}`} 
        showBackButton 
      />
      
      {/* Título con botón de ayuda */}
      <div className={isMobile ? "profile-title-container-mobile" : "profile-title-container"}>
        <h1 className={isMobile ? "profile-title-mobile" : "profile-title"}>
          👤 {isOwnProfile ? "Mi Perfil" : `Perfil de ${user.name}`}
        </h1>
      </div>

      {/* Botón de amistad (solo en perfil ajeno, usuario logueado) */}
      {authUser && !isOwnProfile && (
        <div style={{
          padding: isMobile ? '0 16px 12px' : '0 24px 16px',
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          {friendshipStatus === 'none' || friendshipStatus === 'rejected' ? (
            <button
              type="button"
              onClick={sendFriendRequest}
              disabled={sendingFriendRequest}
              style={{
                padding: '10px 20px',
                background: sendingFriendRequest ? '#555' : '#00E676',
                color: '#111',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: sendingFriendRequest ? 'not-allowed' : 'pointer',
              }}
            >
              {sendingFriendRequest ? 'Enviando...' : '🤝 Agregar amigo'}
            </button>
          ) : friendshipStatus === 'pending_sent' ? (
            <span style={{ padding: '10px 20px', color: '#aaa', fontSize: 14 }}>✓ Solicitud enviada</span>
          ) : friendshipStatus === 'friends' ? (
            <span style={{ padding: '10px 20px', color: '#00E676', fontSize: 14 }}>✓ Amigos</span>
          ) : friendshipStatus === 'pending_received' ? (
            <span style={{ padding: '10px 20px', color: '#FFD700', fontSize: 14 }}>
              Tenés una solicitud de este usuario —{' '}
              <button
                type="button"
                onClick={() => navigate('/friends')}
                style={{ background: 'none', border: 'none', color: '#00E676', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}
              >
                Ver en Amigos
              </button>
            </span>
          ) : null}
        </div>
      )}

      <div className={isMobile ? "profile-content-mobile" : "profile-content"}>
        {/* Pestañas - Ahora arriba del contenido */}
        <ProfileTabs
          activeTab={activeTab}
          activeSubSection={activeSubSection}
          onTabChange={(tab: TabType, subSection?: SubSectionType) => {
            setActiveTab(tab);
            if (subSection) {
              setActiveSubSection(subSection);
            } else {
              // Establecer sub-sección por defecto según el tab
              switch (tab) {
                case 'profile':
                  setActiveSubSection('fifa-card');
                  break;
                case 'stats':
                  setActiveSubSection('main-stats');
                  break;
                case 'rewards':
                  setActiveSubSection('badges');
                  break;
                case 'history':
                  setActiveSubSection(null);
                  break;
              }
            }
          }}
          isMobile={isMobile}
        />

        {/* Contenido de las pestañas */}
        {activeTab === 'profile' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'fifa-card' && (
                  <div className="profile-section-mobile">
                    <ProfileHeader
                      user={user}
                      stats={stats}
                      onEditProfile={() => {}} // No permitir edición en perfil público
                      onAvatarChange={() => {}} // No permitir cambio de avatar en perfil público
                      isMobile={isMobile}
                      showEditButtons={false} // Ocultar botones en perfil público
                    />
                  </div>
                )}
                
                {activeSubSection === 'reviews' && (
                  <div className="profile-section-mobile">
                    <UserReviewsCard 
                      reviews={userReviews}
                      isMobile={isMobile}
                      isPublicProfile={true}
                    />
                  </div>
                )}
              </>
            ) : (
              // Layout desktop original
              <>
                {/* Layout principal: FIFA Card + Reviews */}
                <div className="profile-main-layout">
                  {/* Columna izquierda - FIFA Card */}
                  <div className="profile-main-column">
                    <ProfileHeader
                      user={user}
                      stats={stats}
                      onEditProfile={() => {}} // No permitir edición en perfil público
                      onAvatarChange={() => {}} // No permitir cambio de avatar en perfil público
                      isMobile={isMobile}
                      showEditButtons={false} // Ocultar botones en perfil público
                    />
                  </div>
                  
                  {/* Columna derecha - Reviews */}
                  <div className="profile-main-column">
                    <UserReviewsCard 
                      reviews={userReviews}
                      isMobile={isMobile}
                      isPublicProfile={true}
                    />
                  </div>
                </div>

                {/* Estadísticas principales */}
                <ProfileStats stats={stats} isMobile={isMobile} />
                
                {/* Layout de dos columnas para desktop */}
                <div className="profile-two-column-layout">
                  {/* Columna izquierda - Partidos Jugados */}
                  <div className="profile-column-left">
                    <MatchHistory
                      matches={matchHistory}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      isMobile={isMobile}
                    />
                  </div>
                  
                  {/* Columna derecha - Tags */}
                  <div className="profile-column-right">
                    {/* Tags más recibidos */}
                    <UserTagsCard
                      gamificationStats={gamificationStats}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Nueva pestaña de Estadísticas */}
        {activeTab === 'stats' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'main-stats' && (
                  <div className="profile-section-mobile">
                    <ProfileStats stats={stats} isMobile={isMobile} />
                  </div>
                )}
                
                {activeSubSection === 'match-history' && (
                  <div className="profile-section-mobile">
                    <MatchHistory
                      matches={matchHistory}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      isMobile={isMobile}
                    />
                  </div>
                )}
                
                {activeSubSection === 'tags' && (
                  <div className="profile-section-mobile">
                    <UserTagsCard
                      gamificationStats={gamificationStats}
                      isMobile={isMobile}
                    />
                  </div>
                )}
              </>
            ) : (
              // Layout desktop - mostrar todo junto
              <>
                <ProfileStats stats={stats} isMobile={isMobile} />
                <div className="profile-two-column-layout">
                  <div className="profile-column-left">
                    <MatchHistory
                      matches={matchHistory}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      isMobile={isMobile}
                    />
                  </div>
                  <div className="profile-column-right">
                    <UserTagsCard
                      gamificationStats={gamificationStats}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Pestaña de Recompensas */}
        {activeTab === 'rewards' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'badges' && (
                  <div className="profile-section-mobile">
                    <AchievementGrid unlocked={achievements} />
                  </div>
                )}
              </>
            ) : (
              // Layout desktop original
              <AchievementGrid unlocked={achievements} />
            )}
          </>
        )}

        {/* Pestaña de Historial */}
        {activeTab === 'history' && (
          <StatsHistory
            userId={user.id}
            isMobile={isMobile}
            useMockData={false}
            isPublicProfile={true}
            hideVenueDetails={friendshipStatus !== 'friends'}
          />
        )}

        {/* Modales */}
        <UserBadges
          badges={userBadges}
          isVisible={showBadges}
          onClose={() => setShowBadges(false)}
        />

        <Rankings
          rankings={rankings}
          isVisible={showRankings}
          onClose={() => setShowRankings(false)}
        />

        <GlobalStats
          isVisible={showGlobalStats}
          onClose={() => setShowGlobalStats(false)}
        />

        {/* Modal de Ayuda Contextual */}
        <SpecificHelpModal 
          isOpen={showHelp} 
          onClose={() => setShowHelp(false)} 
          context="profile"
        />

      </div>
    </div>
  );
};

export default PublicProfilePage;